import type { World } from '../sim/types';
import { MONTH_DAYS, dollars, monthsInHand, oneTimeLastMonth, recurringPerMonth, surveyCost } from '../sim/operations';
import { ECONOMY } from '../sim/economy';
import { civilFromEpochDay, dateLabel, daysInMonth, epochDayFromCivil, epochDayOf } from '../sim/calendar';
import { REEF_ISLANDS } from '../sim/reef';
import { patrolHoursAdded } from '../sim/management';
import { escapeHtml } from './format';

/**
 * The runway in words (M289, the playtest's runway point): the months the funds hold at the costs that recur, said to be
 * those, so a month of surveys no longer reads as the funds halved.
 */
export function runwayWords(world: World): string {
  const months = monthsInHand(world);
  // In debt the funds hold no month (review M289's: "about -1 months"); its callers say the debt.
  if (!Number.isFinite(months) || world.operations.funds < 0) return '';
  const n = Math.floor(months);
  return `about ${n} ${n === 1 ? 'month' : 'months'} of fixed costs`;
}

/** The runway explained: the fixed costs a month, line by line, and the one-time work apart, the last month's spent on it. */
export function runwayHtml(world: World): string {
  const r = recurringPerMonth(world);
  const parts = [['salaries', r.salaries], ['boat upkeep', r.boats], ['patrols', r.patrols], ['volunteers', r.volunteers]]
    .filter(([, v]) => (v as number) > 0).map(([k, v]) => `${k} ${dollars(v as number)}`);
  const once = oneTimeLastMonth(world).total;
  const months = world.operations.months.length > 0;
  return `<section class="runway"><p><strong>Fixed costs: ${escapeHtml(dollars(r.total))} a month</strong> <span class="row-note">${escapeHtml(parts.join(', '))}</span></p>
    <p class="row-note">One-time costs are paid when the work is sent: a survey's field costs and fuel, a rule's consultation, a boat. ${months ? `Last month's: ${escapeHtml(dollars(once))}.` : ''} The months in hand count the fixed costs alone.</p></section>`;
}

/**
 * The next operating grant's day, an epoch day: the first 1 July after `today` (a day's operations pay it after the day
 * has begun, so on 1 July it has come).
 */
export function nextGrantDay(today: number): number {
  const year = civilFromEpochDay(today).year;
  const thisYear = epochDayFromCivil(year, ECONOMY.grantDay.month, ECONOMY.grantDay.day);
  return thisYear > today ? thisYear : epochDayFromCivil(year + 1, ECONOMY.grantDay.month, ECONOMY.grantDay.day);
}

/** The money to the next grant: its day, the fixed costs to it, the trips planned and not yet paid, and what is left. */
export interface YearMoney { grantDay: number; fixed: number; planned: number; spare: number }

/**
 * The year's money (M324, the casual year's H6, the main session's playtest: "Money stops mattering on 1 July"): the funds
 * against what is owed to the next grant, so a cost reads against the year's plan rather than as months of fixed costs
 * the grant has made into years. The fixed costs to it as committed today and as the accounts pay them: the staff's
 * pay and each boat's upkeep at each month's end before it, each island's patrols by the day to its term's end where one
 * is set, the volunteers by the day; the boats' plan at each trip's cost (`surveyCost`, paid when it sails). The
 * tourism partners' fees are left out, as a cost's reckoning leaves them (they follow the coral's count).
 */
export function yearMoney(world: World): YearMoney {
  const today = epochDayOf(world);
  const grantDay = nextGrantDay(today);
  const days = grantDay - today;
  const r = recurringPerMonth(world);
  const ops = world.operations;
  // Each one's pay owed to the last month's end before the grant, as `pay` pays it at each month's end, a day at the
  // month's pay over its days, from the day after the last paid (its review: a share of today's month by the days left
  // had left the month already worked unpaid, "to spare" the day before June's accounts went into debt).
  const lastEnd = grantDay - 1;
  const salaries = ops.staff.reduce((n, member) => {
    let owed = 0;
    for (let d = member.paidTo + 1; d <= lastEnd; d++) {
      const c = civilFromEpochDay(d);
      owed += ECONOMY.monthlyPay[member.role] / daysInMonth(c.year, c.month);
    }
    return n + owed;
  }, 0);
  // The boats' upkeep at each month's end before the grant, today's own paid if it is one, as `stepOperations` charges
  // it: the first boat's month, and the second's from the day it is afloat, a boat on order counted from its delivery
  // (its second review: it had dropped the spare by itself on the day it came).
  const second = ops.projects.find((p) => p.kind === 'boat');
  let boats = 0;
  for (let d = today + 1; d <= lastEnd; d++) {
    const c = civilFromEpochDay(d);
    if (c.day !== daysInMonth(c.year, c.month)) continue;
    const afloat = second && second.readyDay <= d ? Math.min(1, (d - second.readyDay + 1) / c.day) : 0;
    boats += Math.round(ECONOMY.boat.upkeepPerMonth * (1 + afloat));
  }
  const m = world.sanctuaries[0]?.management;
  // Each island's officers' hours a year at its level, paid by the day (`patrolCost`), to its term's last day or the grant.
  const patrols = m ? REEF_ISLANDS.reduce((n, island, i) => {
    const until = m.patrolUntil[i] ?? null;
    const paid = until === null ? days : Math.max(0, Math.min(days, until - today));
    return n + (patrolHoursAdded(island, m.patrols[i] ?? 0) * ECONOMY.officerHour * paid) / 365;
  }, 0) : 0;
  // The volunteers paid by the day, a year's over its days (`stepOperations`).
  const fixed = Math.round(salaries + boats + (r.volunteers * days) / MONTH_DAYS + patrols);
  const planned = world.operations.plan.reduce((n, p) => n + surveyCost(p.kind, p.days), 0);
  return { grantDay, fixed, planned, spare: world.operations.funds - fixed - planned };
}

/**
 * The year's money in a sentence, and after a cost where one is given (`cost`, paid before the next grant): what is left
 * to spare to the next grant after its fixed costs and the trips planned, or how far short.
 */
export function spareWords(world: World, cost = 0): string {
  const y = yearMoney(world);
  const owed = y.planned > 0 ? 'the fixed costs and the trips planned' : 'the fixed costs';
  const left = (x: number): string => (x >= 0 ? dollars(x) : `${dollars(-x)} short`);
  return `Until the grant on ${dateLabel(y.grantDay)}: ${y.spare >= 0 ? `${dollars(y.spare)} to spare` : `${dollars(-y.spare)} short`} after ${owed}${cost > 0 ? `, ${left(y.spare - cost)} after it` : ''}.`;
}

/** The year's money in a few words, for the funds' rows: what is spare to the next grant, or short. */
export function spareShort(world: World): string {
  const y = yearMoney(world);
  return `${y.spare >= 0 ? `${dollars(y.spare)} spare` : `${dollars(-y.spare)} short`} to the grant on ${dateLabel(y.grantDay)}`;
}
